import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
// import ReCAPTCHA from 'react-google-recaptcha'; // Commented out for future implementation
import API_CONFIG from '../config/api';

const Login: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginMode, setLoginMode] = useState<'otp' | 'password'>('otp');
  // const [captchaToken, setCaptchaToken] = useState<string | null>(null); // Commented out for future implementation
  const navigate = useNavigate();
  const location = useLocation();
  // const recaptchaRef = useRef<any>(null); // Commented out for future implementation
  
  // Check if running on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Get the redirect path if user was redirected from a protected route
  const from = (location.state as { from?: string })?.from || '/home';
  
  // API base URL from configuration
  const API_URL = API_CONFIG.baseURL;

  // Commented out for future implementation
  // const handleCaptchaChange = (token: string | null) => {
  //   setCaptchaToken(token);
  // };

  // Initialize reCAPTCHA on component mount
  // useEffect(() => {
  //   if (!isLocalhost && recaptchaRef.current) {
  //     // reCAPTCHA component mounted
  //   }
  // }, [isLocalhost]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      // Commented out for future implementation
      // Execute reCAPTCHA v3 for production
      // let captchaTokenToSend = null;
      // if (!isLocalhost && recaptchaRef.current) {
      //   try {
      //     captchaTokenToSend = await recaptchaRef.current.executeAsync('login');
      //   } catch (captchaError) {
      //     // Continue without reCAPTCHA for now
      //   }
      // }
      let captchaTokenToSend = null; // No reCAPTCHA for now
      
      if (loginMode === 'otp') {
        // Send request to API to get verification code
        const response = await axios.post(`${API_URL}/users/send-code`, {
          mobile: phoneNumber,
          captchaToken: captchaTokenToSend
        });
        
        // If successful, navigate to verify page
        if (response.data.success) {
          navigate('/verify', { state: { phoneNumber, from } });
        } else {
          setError(response.data.message || 'Failed to send verification code');
        }
      } else {
        // Password login
        const response = await axios.post(`${API_URL}/users/login-password`, {
          mobile: phoneNumber,
          password: password,
          captchaToken: captchaTokenToSend
        });
        
        if (response.data.success) {
          // Store token and user data
          const token = response.data.token.startsWith('Bearer ') 
            ? response.data.token.substring(7) 
            : response.data.token;
            
          localStorage.setItem('authToken', token);
          localStorage.setItem('userData', JSON.stringify(response.data.data));
          
          // Check if user is registered
          const userData = response.data.data;
          if (!userData.registered) {
            navigate('/complete-registration', { 
              state: { userData, from } 
            });
          } else {
            navigate(from);
          }
        } else {
          setError(response.data.message || 'Login failed');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect to server');
      // Reset captcha on error - Commented out for future implementation
      // if (recaptchaRef.current) {
      //   recaptchaRef.current.reset();
      //   setCaptchaToken(null);
      // }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white md:bg-slate-50 flex h-dvh">
      <main className="w-full">
        <div className="max-w-[480px] mx-auto select-none flex flex-col md:justify-center md:h-dvh">
          <form className="flex flex-col items-start pt-8 md:p-12 rounded-md md:border-1 border-slate-200 bg-white" onSubmit={handleSubmit}>
            <div className="px-4 md:p-0 w-full">
              <img src="/assets/img/logoRazi.png" alt="Razi Logo" width="42px" height="42px" />

              <h1 className="text-xl font-bold text-slate-900 mt-2">ورود/ ثبت نام در آزمایشگاه رازی رشت</h1>
              <p className="text-sm text-slate-700 mt-2 mb-4">
                برای ورود به رازی شماره همراه خود را وارد کنید.
              </p>
              <div className="w-full">
                <input 
                  placeholder="۰۹xxxxxxxxx" 
                  autoComplete="tel" 
                  inputMode="numeric" 
                  maxLength={11} 
                  pattern="\d+" 
                  name="tel" 
                  tabIndex={0} 
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)} 
                  className="w-full border border-slate-200 py-2 px-4 rounded-lg text-md min-h-[54px] focus:border-[#00bcd4] focus:outline-none"
                  required
                />
              </div>
              
              {loginMode === 'password' && (
                <div className="w-full mt-4">
                  <input 
                    placeholder="رمز عبور" 
                    autoComplete="current-password" 
                    name="password" 
                    tabIndex={0} 
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)} 
                    className="w-full border border-slate-200 py-2 px-4 rounded-lg text-md min-h-[54px] focus:border-[#00bcd4] focus:outline-none"
                    required
                  />
                </div>
              )}
              
              <p className="text-sm text-slate-600 mt-4 mb-0"> ورود و ثبت‌ نام در رازی به منزله‌ پذیرفتن <a href="/faq/policy/" target="_blank" className="text-blue-700"> قوانین و مقررات </a> و <a href="/faq/privacy/" target="_blank" className="text-blue-700"> قوانین حریم خصوصی </a> می‌باشد. </p>
              
              {/* reCAPTCHA - Commented out for future implementation */}
              {/* {!isLocalhost && (
                <div className="flex justify-center mt-4">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6Lc7rcYrAAAAAI1b9LCiEuVnQAOEBYJ_ymEnhnx9"
                    onChange={handleCaptchaChange}
                    theme="light"
                    size="invisible"
                    onErrored={() => {
                      setError('خطا در بارگذاری reCAPTCHA');
                    }}
                    onExpired={() => {
                      // reCAPTCHA expired
                    }}
                  />
                </div>
              )} */}
              
              {error && (
                <p className="text-red-500 text-sm my-2">{error}</p>
              )}
              
            </div>
            <div className="fixed right-0 left-0 bottom-0 p-4 border-t-1 border-slate-200 md:relative md:p-0 md:border-0 md:mt-8 md:w-full z-90">
              <button 
                  tabIndex={0} 
                  type="submit" 
                  disabled={isLoading || (loginMode === 'password' && !password)}
                  className="w-full bg-[#00bcd4] hover:bg-[#00acc1] text-white py-2 px-4 rounded-lg text-md min-h-[48px] cursor-pointer bg-[#1a237e]"
                >
                  {isLoading ? 'در حال ارسال...' : (loginMode === 'otp' ? 'ادامه' : 'ورود')}
                </button>
                <div className="w-full h-1 bg-slate-200 my-4"></div>
                <button 
                  type="button"
                  onClick={() => {
                    setLoginMode(loginMode === 'otp' ? 'password' : 'otp');
                    setError('');
                    setPassword('');
                    // Reset captcha when switching modes - Commented out for future implementation
                    // if (recaptchaRef.current) {
                    //   recaptchaRef.current.reset();
                    //   setCaptchaToken(null);
                    // }
                  }}
                  className="w-full bg-[#00bcd4] hover:bg-[#00acc1] text-white py-2 px-4 rounded-lg text-md min-h-[48px] cursor-pointer bg-[#1a237e]"
                >
                   {loginMode === 'otp' ? 'ورود با کلمه عبور' : 'ورود با رمز یک بار مصرف'}
                </button>
              </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Login;
